"use client";

import { ArrowRight, Star, StarHalf } from "lucide-react";
import RolePill from "./RolePill";

// concentric ring diameters — two groups, one anchored left, one right, so the
// arcs bow inward from each edge (not a single centered system)
const rings = [
  {
    id: "first",
    size: "w-[80px] h-[80px] lg:w-[110px] lg:h-[110px] xl:w-[200px] xl:h-[200px]",
    left: "-left-[40px] lg:-left-[55px] xl:-left-[100px]",
    right: "-right-[40px] lg:-right-[55px] xl:-right-[100px]",
    top: "-top-[40px] lg:-top-[55px] xl:-top-[100px]",
  },
  {
    id: "second",
    size: "w-[180px] h-[180px] lg:w-[260px] lg:h-[260px] xl:w-[400px] xl:h-[400px]",
    left: "-left-[90px] lg:-left-[130px] xl:-left-[200px]",
    right: "-right-[90px] lg:-right-[130px] xl:-right-[200px]",
    top: "-top-[90px] lg:-top-[130px] xl:-top-[200px]",
  },
  {
    id: "third",
    size: "w-[300px] h-[300px] lg:w-[440px] lg:h-[440px] xl:w-[600px] xl:h-[600px]",
    left: "-left-[150px] lg:-left-[220px] xl:-left-[300px]",
    right: "-right-[150px] lg:-right-[220px] xl:-right-[300px]",
    top: "-top-[150px] lg:-top-[220px] xl:-top-[300px]",
  },
];
// scattered decorative dots: [left%, top%, size px, filled?]
const dots = [
  ["6%", "38%", 5],
  ["14%", "35%", 5],
  ["5%", "63%", 5],
  ["2%", "50%", 5],
  ["12%", "70%", 5],

  ["94%", "35%", 5],
  ["88%", "30%", 5],
  ["96%", "50%", 5],
  ["93%", "63%", 5],
  ["89%", "72%", 5],
];

const HeroSection = () => {
  return (
    <section className="relative flex xl:min-h-[760px] w-full xl:items-center overflow-hidden px-5 py-16 sm:px-6 lg:px-20 lg:py-24">
      {/* left ring group — centered just off the left edge */}
      <div className="hidden md:block pointer-events-none absolute left-0 top-1/2 -translate-y-1/2">
        {rings.map((ring) => (
          <div
            key={ring.id}
            className={`absolute rounded-full border border-[#C9CDE0]/40 ${ring.size} ${ring.left} ${ring.top}`}
          />
        ))}
      </div>

      {/* right ring group — centered just off the right edge */}
      <div className="hidden md:block  pointer-events-none absolute right-0 top-1/2 -translate-y-1/2">
        {rings.map((ring) => (
          <div
            key={ring.id}
            className={`absolute rounded-full border border-[#C9CDE0]/40 ${ring.size} ${ring.right} ${ring.top}`}
          />
        ))}
      </div>

      {/* decorative dots */}
      {dots.map(([left, top, size], i) => (
        <span
          key={i}
          className={`hidden md:block pointer-events-none absolute rounded-full bg-[#7F9BE9]/30`}
          style={{ left, top, width: size, height: size }}
        />
      ))}

      {/* floating collaborator pills */}
      <RolePill
        label="Brand Designer"
        side="left"
        avatar="https://i.pravatar.cc/80?img=12"
        className="left-[5%] top-[20%] hidden lg:block"
      />
      <RolePill
        label="Creative Lead"
        side="left"
        avatar="https://i.pravatar.cc/80?img=5"
        className="left-[10%] top-[47%] hidden lg:block"
      />
      <RolePill
        label="Growth strategist"
        side="left"
        avatar="https://i.pravatar.cc/80?img=33"
        className="-left-[2%] top-[68%] hidden xl:block"
      />
      <RolePill
        label="Content Strategist"
        side="right"
        avatar="https://i.pravatar.cc/80?img=47"
        className="right-[5%] top-[20%] hidden lg:block"
      />
      <RolePill
        label="Content Strategist"
        side="right"
        avatar="https://i.pravatar.cc/80?img=9"
        className="right-[10%] top-[52%] hidden lg:block"
      />
      <RolePill
        label="Product developer"
        side="right"
        avatar="https://i.pravatar.cc/80?img=60"
        className="-right-[2%] top-[72%] hidden xl:block"
      />

      {/* center content */}
      <div className="relative z-10 mx-auto flex max-w-[760px] 2xl:max-w-[1200px] flex-col items-center text-center">
        <h2 className="solution-heading ">
          One{" "}
          <span className=" bg-clip-text text-transparent bg-gradient-to-r from-[#202020] to-[#8F8F8F]/90  ">
            workspace.
          </span>
          <br />
          Every brand,{" "}
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#202020] to-[#8F8F8F]/90">
            every team.
          </span>
        </h2>

        <p className="mt-5 body-secondary-text  md:max-w-[500px]  lg:max-w-[500px] xl:max-w-[700px] ">
          Switch between organizations, manage multiple brand workspaces, and
          keep every asset ads, references, brand guidelines in one place your
          whole team can access.
        </p>

        <a
          href="https://app.imagive.ai?redirect=/users/brands"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary group mt-9 inline-flex items-center justify-center gap-2 font-medium"
        >
          Set up your workspace
          <ArrowRight
            size={18}
            className="transition-transform duration-300 group-hover:translate-x-1"
          />
        </a>

        {/* rating */}
        <div className="mt-4 flex items-center gap-2">
          <div className="flex items-center gap-0.5">
            {[0, 1, 2, 3].map((i) => (
              <Star
                key={i}
                size={17}
                className="fill-[#FFB400] text-[#FFB400]"
              />
            ))}
            <StarHalf size={17} className="fill-[#FFB400] text-[#FFB400]" />
          </div>
          <span className="text-[14px] text-[#6D6D75]">
            <span className="font-semibold text-[#1D1D1F]">25,000+</span>{" "}
            reviews from
          </span>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
